import { useTranslation } from "react-i18next";
import { Heart, MapPin, Mail } from "lucide-react";
import { Link } from "react-router";
import { ScrollReveal } from "@/hooks/use-scroll-animation";

export const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary text-primary-foreground relative overflow-hidden hex-pattern hex-pattern-dark">
      {/* Gold Architectural Divider at Top */}
      <div className="gold-divider absolute top-0 left-0 right-0" />
      
      <div className="container mx-auto px-4 py-10 lg:py-12">
        <ScrollReveal direction="up" delay={0}>
          <div className="max-w-2xl mx-auto text-center space-y-6">
            {/* Logo/Title */}
            <div className="space-y-2">
              <h3 className="text-xl lg:text-2xl font-display font-normal tracking-wide">
                {t('footer.title')}
              </h3>
              <p className="text-sm text-primary-foreground/60 font-technical uppercase tracking-[0.15em]">
                {t('footer.description')}
              </p>
              <div className="flex items-center justify-center gap-2 text-primary-foreground/70">
                <MapPin className="w-4 h-4" />
                <a 
                  href="https://maps.google.com/?q=77-03+Main+Street,+Flushing,+NY+11367"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm hover:text-secondary transition-colors underline-offset-2 hover:underline"
                  title="Open in Google Maps"
                >
                  77-03 Main Street, Flushing, NY 11367-1914
                </a>
              </div>
              <div className="flex items-center justify-center gap-2 text-primary-foreground/70">
                <Mail className="w-4 h-4" />
                <a 
                  href="mailto:donations@chabaddonations.org" 
                  className="text-sm hover:text-secondary transition-colors"
                  title="Contact Chabad Flushing for Chabad donations"
                >
                  donations@chabaddonations.org
                </a>
              </div>
              <div className="flex items-center justify-center gap-2 text-primary-foreground/70">
                <span className="text-sm">📞</span>
                <a 
                  href="tel:+19297424666" 
                  className="text-sm hover:text-secondary transition-colors"
                >
                  +1 (929) 742-4666
                </a>
              </div>
            </div>
            
            {/* Gold Divider */}
            <div className="gold-divider max-w-[4rem] mx-auto" />

            {/* Tax Notice */}
            <div className="space-y-2 text-sm text-primary-foreground/70">
              <p>{t('footer.taxNotice')}</p>
              <p>{t('footer.taxDeductible')}</p>
            </div>

            {/* Legal Links */}
            <div className="flex items-center justify-center gap-4 text-sm flex-wrap">
              <Link to="/privacy" className="text-primary-foreground/60 hover:text-secondary transition-colors">
                {t('footer.privacyPolicy')}
              </Link>
              <span className="text-primary-foreground/30">|</span>
              <Link to="/terms" className="text-primary-foreground/60 hover:text-secondary transition-colors">
                {t('footer.terms')}
              </Link>
              <span className="text-primary-foreground/30">|</span>
              <Link to="/cookies" className="text-primary-foreground/60 hover:text-secondary transition-colors">
                {t('footer.cookiePolicy')}
              </Link>
              <span className="text-primary-foreground/30">|</span>
              <a
                href="https://billing.stripe.com/p/login/6oU4gy6UW2b2gOtgS9enS00"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-foreground/60 hover:text-secondary transition-colors"
              >
                Manage My Donation
              </a>
            </div>
            
            {/* Copyright */}
            <div className="pt-4 flex items-center justify-center gap-1.5 text-sm text-primary-foreground/50">
              <span>{t('footer.copyright', { year: currentYear })}</span>
              <Heart className="w-3.5 h-3.5 text-secondary fill-secondary" />
            </div>
          </div>
        </ScrollReveal>
      </div>
    </footer>
  );
};
